import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Link,
  Divider
} from '@mui/material';
import { Add, Edit, Delete, OpenInNew, School } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const emptyForm = {
  name: '',
  url: '',
  description: '',
  sortOrder: 0
};

const FleetwrightTutorials = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [tutorials, setTutorials] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [tutorialToDelete, setTutorialToDelete] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;

  const fetchTutorials = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/fleetwright-tutorials`);
      setTutorials(response.data.tutorials || []);
    } catch (error) {
      console.error('Error fetching tutorials:', error);
      showToast(error.response?.data?.message || 'Failed to load tutorials', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchTutorials();
  }, [fetchTutorials]);

  const handleOpenAdd = () => {
    setSelectedTutorial(null);
    setFormData(emptyForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenEdit = (tutorial) => {
    setSelectedTutorial(tutorial);
    setFormData({
      name: tutorial.name || '',
      url: tutorial.url || '',
      description: tutorial.description || '',
      sortOrder: tutorial.sortOrder ?? 0
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenDelete = (tutorial) => {
    setTutorialToDelete(tutorial);
    setDeleteDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.url.trim()) {
      errors.url = 'URL is required';
    } else {
      try {
        const parsed = new URL(formData.url.trim());
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          errors.url = 'URL must start with http:// or https://';
        }
      } catch {
        errors.url = 'Enter a valid URL (include http:// or https://)';
      }
    }
    if (formData.sortOrder !== '' && Number.isNaN(Number(formData.sortOrder))) {
      errors.sortOrder = 'Sort order must be a number';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      name: formData.name.trim(),
      url: formData.url.trim(),
      description: formData.description.trim(),
      sortOrder: Number(formData.sortOrder) || 0
    };

    try {
      if (selectedTutorial) {
        await axios.put(`${API_URL}/fleetwright-tutorials/${selectedTutorial._id}`, payload);
        showToast('Tutorial updated successfully', 'success');
      } else {
        await axios.post(`${API_URL}/fleetwright-tutorials`, payload);
        showToast('Tutorial added successfully', 'success');
      }
      setDialogOpen(false);
      setSelectedTutorial(null);
      fetchTutorials();
    } catch (error) {
      console.error('Error saving tutorial:', error);
      const apiErrors = error.response?.data?.errors;
      if (apiErrors?.length) {
        const mapped = {};
        apiErrors.forEach((e) => {
          if (e.path) mapped[e.path] = e.msg;
        });
        setFormErrors(mapped);
      }
      showToast(error.response?.data?.message || 'Failed to save tutorial', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API_URL}/fleetwright-tutorials/${tutorialToDelete._id}`);
      showToast('Tutorial removed successfully', 'success');
      setDeleteDialogOpen(false);
      setTutorialToDelete(null);
      fetchTutorials();
    } catch (error) {
      console.error('Error deleting tutorial:', error);
      showToast(error.response?.data?.message || 'Failed to remove tutorial', 'error');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <School color="primary" fontSize="large" />
          <Typography variant="h4">Fleetwright Tutorials</Typography>
        </Stack>
        {isSuperAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
            Add Tutorial
          </Button>
        )}
      </Stack>

      {isSuperAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          As Super Admin, you can add and manage tutorial links. All users can view and open them from this page.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Typography color="text.secondary">Loading tutorials...</Typography>
        ) : tutorials.length === 0 ? (
          <Typography color="text.secondary">
            No tutorials have been added yet.
            {isSuperAdmin ? ' Use "Add Tutorial" to publish your first link.' : ''}
          </Typography>
        ) : (
          <List disablePadding>
            {tutorials.map((tutorial, index) => (
              <React.Fragment key={tutorial._id}>
                {index > 0 && <Divider />}
                <ListItem
                  alignItems="flex-start"
                  sx={{ py: 2, pr: isSuperAdmin ? 14 : 2 }}
                  secondaryAction={
                    isSuperAdmin ? (
                      <Stack direction="row" spacing={0.5}>
                        <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEdit(tutorial)}>
                          <Edit />
                        </IconButton>
                        <IconButton edge="end" aria-label="delete" onClick={() => handleOpenDelete(tutorial)}>
                          <Delete />
                        </IconButton>
                      </Stack>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={
                      <Link
                        href={tutorial.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {tutorial.name}
                        <OpenInNew fontSize="small" />
                      </Link>
                    }
                    secondary={
                      <>
                        {tutorial.description && (
                          <Typography component="span" variant="body2" color="text.secondary" display="block">
                            {tutorial.description}
                          </Typography>
                        )}
                        <Typography component="span" variant="caption" color="text.secondary" display="block">
                          {tutorial.url}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedTutorial ? 'Edit Tutorial' : 'Add Tutorial'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              error={!!formErrors.name}
              helperText={formErrors.name}
              fullWidth
              required
            />
            <TextField
              label="URL"
              value={formData.url}
              onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
              error={!!formErrors.url}
              helperText={formErrors.url || 'Full link including https://'}
              fullWidth
              required
            />
            <TextField
              label="Description (optional)"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Sort order"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData((prev) => ({ ...prev, sortOrder: e.target.value }))}
              error={!!formErrors.sortOrder}
              helperText={formErrors.sortOrder || 'Lower numbers appear first'}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleFormSubmit}>
            {selectedTutorial ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Tutorial</DialogTitle>
        <DialogContent>
          <Typography>
            Remove &quot;{tutorialToDelete?.name}&quot;? Users will no longer see this link.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FleetwrightTutorials;
